import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../design/tokens';
import { SocialSettings } from '../../types';
import { FiSave, FiSmartphone, FiPlus, FiTrash2 } from 'react-icons/fi';

const Section = styled.section`
  background: ${tokens.colors.adminSurface};
  border-radius: 12px;
  padding: ${tokens.spacing.lg};
  margin-bottom: ${tokens.spacing.lg};
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.spacing.lg};
  padding-bottom: ${tokens.spacing.sm};
  border-bottom: 1px solid ${tokens.colors.adminBorder};
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${tokens.colors.adminText};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
`;

const AppForm = styled.div`
  padding: ${tokens.spacing.lg};
`;

const FormGroup = styled.div`
  margin-bottom: ${tokens.spacing.xl};
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 600;
  color: ${tokens.colors.adminText};
  margin-bottom: ${tokens.spacing.sm};
`;

const FormInput = styled.input`
  width: 100%;
  padding: ${tokens.spacing.md};
  border: 1px solid ${tokens.colors.adminBorder};
  border-radius: 6px;
  font-size: 14px;
  background: ${tokens.colors.adminSurface};
  color: ${tokens.colors.adminText};
  &:focus {
    outline: none;
    border-color: ${tokens.colors.adminAccent};
  }
`;

const InstructionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.md};
`;

const InstructionItem = styled.div`
  display: flex;
  gap: ${tokens.spacing.md};
  align-items: center;
`;

const RemoveBtn = styled.button`
  background: #fee2e2;
  color: #dc2626;
  border: none;
  padding: ${tokens.spacing.sm};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #fecaca;
  }
`;

const AddBtn = styled.button`
  background: ${tokens.colors.adminAccent}15;
  color: ${tokens.colors.adminAccent};
  border: 1px dashed ${tokens.colors.adminAccent};
  padding: ${tokens.spacing.md};
  border-radius: 6px;
  width: 100%;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${tokens.spacing.sm};
  margin-top: ${tokens.spacing.md};
  &:hover {
    background: ${tokens.colors.adminAccent}25;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: ${tokens.spacing.md};
  justify-content: flex-end;
  margin-top: ${tokens.spacing.xl};
`;

const SaveBtn = styled.button`
  background: ${tokens.colors.adminAccent};
  color: white;
  border: none;
  padding: ${tokens.spacing.md} ${tokens.spacing.xl};
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
  &:hover {
    background: #7a04b8;
  }
`;

interface AdminAppDownloadSettingsProps {
  settings: Partial<SocialSettings>;
  onUpdate: (settings: Partial<SocialSettings>) => void;
  onSave: () => void;
}

const AdminAppDownloadSettings: React.FC<AdminAppDownloadSettingsProps> = ({ settings, onUpdate, onSave }) => {
  // Parse instructions if it's a string, otherwise use as array
  let instructions: string[] = [];
  try {
    if (typeof settings.instructions === 'string') {
      instructions = JSON.parse(settings.instructions);
    } else if (Array.isArray(settings.instructions)) {
      instructions = settings.instructions;
    }
  } catch (e) {
    console.error('Error parsing instructions:', e);
    instructions = [];
  }

  const handleAddInstruction = () => {
    const newInstructions = [...instructions, ''];
    onUpdate({ ...settings, instructions: JSON.stringify(newInstructions) });
  };

  const handleInstructionChange = (index: number, value: string) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    onUpdate({ ...settings, instructions: JSON.stringify(newInstructions) });
  };

  const handleRemoveInstruction = (index: number) => {
    const newInstructions = instructions.filter((_: string, i: number) => i !== index);
    onUpdate({ ...settings, instructions: JSON.stringify(newInstructions) });
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>
          {React.createElement(FiSmartphone as any, { size: 24 })}
          Download de la App
        </SectionTitle>
      </SectionHeader>
      <AppForm>
        <FormGroup>
          <FormLabel>URL de App Store/Play Store</FormLabel>
          <FormInput
            type="url"
            value={settings.app_download_url || ''}
            onChange={(e) => onUpdate({ ...settings, app_download_url: e.target.value })}
            placeholder="https://play.google.com/store/apps/details?id=seu.app"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Texto del Botón de Descarga</FormLabel>
          <FormInput
            type="text"
            value={settings.download_button_text || ''}
            onChange={(e) => onUpdate({ ...settings, download_button_text: e.target.value })}
            placeholder="Descargar Ahora"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>URL del Video de YouTube (Instrucciones)</FormLabel>
          <FormInput
            type="url"
            value={settings.youtube_video_url || ''}
            onChange={(e) => onUpdate({ ...settings, youtube_video_url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>Instrucciones Paso a Paso</FormLabel>
          <InstructionsList>
            {instructions.map((instruction: string, index: number) => (
              <InstructionItem key={index}>
                <FormInput
                  type="text"
                  value={instruction}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  placeholder={`Paso ${index + 1}`}
                />
                <RemoveBtn onClick={() => handleRemoveInstruction(index)} title="Eliminar paso">
                  {React.createElement(FiTrash2 as any, { size: 16 })}
                </RemoveBtn>
              </InstructionItem>
            ))}
          </InstructionsList>
          <AddBtn onClick={handleAddInstruction}>
            {React.createElement(FiPlus as any, { size: 16 })}
            Añadir Paso
          </AddBtn>
        </FormGroup>

        <FormActions>
          <SaveBtn onClick={onSave}>
            {React.createElement(FiSave as any, { size: 16 })}
            Guardar Cambios
          </SaveBtn>
        </FormActions>
      </AppForm>
    </Section>
  );
};

export default AdminAppDownloadSettings;
